import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Coins,
  Eye,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCcw,
  Search,
  Trash2,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useActiveProgramCurrency } from "../programs/program-scope";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

type RuleKind = "earn" | "redeem";
type RuleTab = "all" | "earn" | "redeem";
type EarnBasis = "points_per_order" | "invoice_percent";
type UsageLimit = "invoice_percent" | "balance_percent" | "none";
type EarnWhileRedeem = "none" | "partial" | "full";

type EarnRule = {
  id: string;
  kind: "earn";
  name: string;
  basis: EarnBasis;
  minimumInvoice: number;
  points: number;
};

type RedeemRule = {
  id: string;
  kind: "redeem";
  name: string;
  minimumPoints: number;
  maxPointsPerOrder: number;
  usageLimit: UsageLimit;
  usagePercent: number | null;
  earnWhileRedeem: EarnWhileRedeem;
};

type PointsRule = EarnRule | RedeemRule;

type RuleForm = {
  kind: RuleKind;
  name: string;
  basis: EarnBasis;
  minimumInvoice: string;
  points: string;
  minimumPoints: string;
  maxPointsPerOrder: string;
  usageLimit: UsageLimit;
  usagePercent: string;
  earnWhileRedeem: EarnWhileRedeem;
};

const fieldClass =
  "h-11 rounded-xl border-slate-200 bg-white shadow-none placeholder:text-slate-400";

const TABS: { id: RuleTab; label: string }[] = [
  { id: "all", label: "All rules" },
  { id: "earn", label: "Earn rules" },
  { id: "redeem", label: "Redemption rules" },
];

const EARN_WHILE_OPTIONS: { value: EarnWhileRedeem; label: string; hint: string }[] = [
  {
    value: "none",
    label: "None",
    hint: "An order that redeems points does not earn any points.",
  },
  {
    value: "partial",
    label: "Partial",
    hint: "Points are earned only on the amount not paid with points.",
  },
  {
    value: "full",
    label: "Full",
    hint: "Points are earned on the full invoice, even when points are redeemed.",
  },
];

const INITIAL_RULES: PointsRule[] = [
  {
    id: "earn-1",
    kind: "earn",
    name: "Fixed points on each order",
    basis: "points_per_order",
    minimumInvoice: 200,
    points: 10,
  },
  {
    id: "earn-2",
    kind: "earn",
    name: "Share of the invoice",
    basis: "invoice_percent",
    minimumInvoice: 500,
    points: 5,
  },
  {
    id: "redeem-1",
    kind: "redeem",
    name: "Checkout redemption",
    minimumPoints: 100,
    maxPointsPerOrder: 2000,
    usageLimit: "invoice_percent",
    usagePercent: 30,
    earnWhileRedeem: "none",
  },
  {
    id: "redeem-2",
    kind: "redeem",
    name: "Balance cap",
    minimumPoints: 50,
    maxPointsPerOrder: 1500,
    usageLimit: "balance_percent",
    usagePercent: 40,
    earnWhileRedeem: "partial",
  },
];

function emptyForm(kind: RuleKind): RuleForm {
  return {
    kind,
    name: "",
    basis: "points_per_order",
    minimumInvoice: "",
    points: "",
    minimumPoints: "",
    maxPointsPerOrder: "",
    usageLimit: "invoice_percent",
    usagePercent: "",
    earnWhileRedeem: "none",
  };
}

function formFromRule(rule: PointsRule): RuleForm {
  if (rule.kind === "earn") {
    return {
      ...emptyForm("earn"),
      kind: "earn",
      name: rule.name,
      basis: rule.basis,
      minimumInvoice: String(rule.minimumInvoice),
      points: String(rule.points),
    };
  }
  return {
    ...emptyForm("redeem"),
    kind: "redeem",
    name: rule.name,
    minimumPoints: String(rule.minimumPoints),
    maxPointsPerOrder: String(rule.maxPointsPerOrder),
    usageLimit: rule.usageLimit,
    usagePercent: rule.usagePercent == null ? "" : String(rule.usagePercent),
    earnWhileRedeem: rule.earnWhileRedeem,
  };
}

function money(value: number, currency: string) {
  const code = currency.trim();
  return `${code ? `${code} ` : ""}${value.toLocaleString("en-IN")}`;
}

function pointsLabel(value: number) {
  return `${value.toLocaleString("en-IN")} pts`;
}

function earnAward(rule: EarnRule) {
  if (rule.basis === "points_per_order") {
    return `${rule.points.toLocaleString("en-IN")} points per order`;
  }
  return `${rule.points}% of the invoice amount`;
}

function usageLabel(rule: RedeemRule) {
  if (rule.usageLimit === "none") return "No usage limit";
  if (rule.usageLimit === "invoice_percent") {
    return `Up to ${rule.usagePercent}% of the invoice`;
  }
  return `Up to ${rule.usagePercent}% of the points balance`;
}

function earnWhileLabel(value: EarnWhileRedeem) {
  return EARN_WHILE_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

function wholeNumber(value: string) {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}

function buildRule(form: RuleForm, id: string): { rule: PointsRule } | { error: string } {
  const name = form.name.trim();
  if (!name) return { error: "Enter a rule name." };

  if (form.kind === "earn") {
    const minimumInvoice = wholeNumber(form.minimumInvoice);
    const points = wholeNumber(form.points);
    if (minimumInvoice == null) {
      return { error: "Enter the minimum invoice amount as a whole number." };
    }
    if (points == null || points < 1) {
      return {
        error:
          form.basis === "points_per_order"
            ? "Enter at least 1 point to give on the order."
            : "Enter the invoice percentage from 1 to 100.",
      };
    }
    if (form.basis === "invoice_percent" && points > 100) {
      return { error: "Invoice percentage cannot be more than 100." };
    }
    return {
      rule: {
        id,
        kind: "earn",
        name,
        basis: form.basis,
        minimumInvoice,
        points,
      },
    };
  }

  const minimumPoints = wholeNumber(form.minimumPoints);
  const maxPointsPerOrder = wholeNumber(form.maxPointsPerOrder);
  if (minimumPoints == null || minimumPoints < 1) {
    return { error: "Enter the minimum points required to redeem." };
  }
  if (maxPointsPerOrder == null || maxPointsPerOrder < 1) {
    return { error: "Enter the maximum points that can be redeemed on one order." };
  }
  if (maxPointsPerOrder < minimumPoints) {
    return { error: "The maximum on one order must be at least the minimum points." };
  }

  let usagePercent: number | null = null;
  if (form.usageLimit !== "none") {
    usagePercent = wholeNumber(form.usagePercent);
    if (usagePercent == null || usagePercent < 1 || usagePercent > 100) {
      return { error: "Enter the usage limit as a percentage from 1 to 100." };
    }
  }

  return {
    rule: {
      id,
      kind: "redeem",
      name,
      minimumPoints,
      maxPointsPerOrder,
      usageLimit: form.usageLimit,
      usagePercent,
      earnWhileRedeem: form.earnWhileRedeem,
    },
  };
}

function TypeBadge({ kind }: { kind: RuleKind }) {
  if (kind === "earn") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800">
        <Coins className="h-3.5 w-3.5" />
        Earn
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-700">
      <RefreshCcw className="h-3.5 w-3.5" />
      Redeem
    </span>
  );
}

function ReadRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-b border-slate-100 py-3 last:border-0">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="mt-1 text-sm font-medium text-slate-900">{value}</p>
    </div>
  );
}

export default function RewardsRules() {
  const { currency } = useActiveProgramCurrency();
  const [rules, setRules] = useState<PointsRule[]>(INITIAL_RULES);
  const [tab, setTab] = useState<RuleTab>("all");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState<RuleForm>(emptyForm("earn"));
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [formError, setFormError] = useState("");
  const [viewing, setViewing] = useState<PointsRule | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PointsRule | null>(null);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rules.filter((rule) => {
      const matchesTab = tab === "all" || rule.kind === tab;
      const matchesSearch = !query || rule.name.toLowerCase().includes(query);
      return matchesTab && matchesSearch;
    });
  }, [rules, search, tab]);

  const openCreate = (kind: RuleKind) => {
    setEditingId(null);
    setForm(emptyForm(kind));
    setFormError("");
    setEditorOpen(true);
  };

  const openEdit = (rule: PointsRule) => {
    setEditingId(rule.id);
    setForm(formFromRule(rule));
    setFormError("");
    setEditorOpen(true);
  };

  const saveRule = () => {
    const result = buildRule(form, editingId ?? `rule-${Date.now()}`);
    if ("error" in result) {
      setFormError(result.error);
      return;
    }
    setRules((current) => {
      if (editingId) {
        return current.map((rule) => (rule.id === editingId ? result.rule : rule));
      }
      return [result.rule, ...current];
    });
    setEditorOpen(false);
    toast.success(editingId ? "Rule updated" : "Rule added");
  };

  const deleteRule = () => {
    if (!pendingDelete) return;
    setRules((current) => current.filter((rule) => rule.id !== pendingDelete.id));
    if (viewing?.id === pendingDelete.id) setViewing(null);
    setPendingDelete(null);
    toast.success("Rule deleted");
  };

  const patch = (next: Partial<RuleForm>) => {
    setForm((current) => ({ ...current, ...next }));
    setFormError("");
  };

  return (
    <div className="mx-auto min-w-0 max-w-6xl space-y-6 pb-4">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm text-slate-500">Rewards</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-950">
            Rules
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-500">
            Set how points are issued on an order, and the policy for redeeming
            them.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-11 rounded-full bg-white"
            onClick={() => openCreate("redeem")}
          >
            Add redemption rule
          </Button>
          <Button
            type="button"
            className="h-11 rounded-full bg-emerald-700 text-white hover:bg-emerald-800"
            onClick={() => openCreate("earn")}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Add earn rule
          </Button>
        </div>
      </header>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-2">
          {TABS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={cn(
                "rounded-full px-3.5 py-1.5 text-sm font-medium",
                tab === item.id
                  ? "bg-emerald-700 text-white"
                  : "bg-white text-slate-600 ring-1 ring-black/[0.06] hover:text-slate-900",
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search rules"
            className="h-10 rounded-full bg-white pl-9"
          />
        </div>
      </div>

      <Card className="rounded-[24px] border-0 bg-white shadow-none ring-1 ring-black/[0.06]">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="pl-5">Rule</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Setup</TableHead>
                  <TableHead>Limits</TableHead>
                  <TableHead className="w-12 pr-4" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-12 text-center text-sm text-slate-500">
                      No rules match this view.
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell className="pl-5">
                        <p className="font-medium text-slate-950">{rule.name}</p>
                      </TableCell>
                      <TableCell>
                        <TypeBadge kind={rule.kind} />
                      </TableCell>
                      <TableCell className="text-sm text-slate-700">
                        {rule.kind === "earn"
                          ? earnAward(rule)
                          : `Min ${pointsLabel(rule.minimumPoints)} to redeem`}
                      </TableCell>
                      <TableCell className="max-w-xs text-sm text-slate-600">
                        {rule.kind === "earn"
                          ? `Invoice at least ${money(rule.minimumInvoice, currency)}`
                          : `Max ${pointsLabel(rule.maxPointsPerOrder)} / order · ${usageLabel(rule)} · Earn while redeem: ${earnWhileLabel(rule.earnWhileRedeem)}`}
                      </TableCell>
                      <TableCell className="pr-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setViewing(rule)}>
                              <Eye className="mr-2 h-4 w-4" />
                              View details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEdit(rule)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-600 focus:text-red-600"
                              onClick={() => setPendingDelete(rule)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Update rule" : form.kind === "earn" ? "Add earn rule" : "Add redemption rule"}
            </DialogTitle>
            <DialogDescription>
              {form.kind === "earn"
                ? "Earn requests stay pending until the program cancellation period ends. This rule decides the points on a qualifying order."
                : "This policy decides when members can spend points, and whether the same order can still earn."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {!editingId ? (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => patch({ kind: "earn" })}
                  className={cn(
                    "rounded-2xl border px-3 py-3 text-left",
                    form.kind === "earn"
                      ? "border-emerald-700 bg-emerald-50"
                      : "border-slate-200 bg-white",
                  )}
                >
                  <p className="text-sm font-semibold text-slate-950">Earn</p>
                  <p className="mt-0.5 text-xs text-slate-500">Points issued on an order</p>
                </button>
                <button
                  type="button"
                  onClick={() => patch({ kind: "redeem" })}
                  className={cn(
                    "rounded-2xl border px-3 py-3 text-left",
                    form.kind === "redeem"
                      ? "border-emerald-700 bg-emerald-50"
                      : "border-slate-200 bg-white",
                  )}
                >
                  <p className="text-sm font-semibold text-slate-950">Redemption</p>
                  <p className="mt-0.5 text-xs text-slate-500">Policy for spending points</p>
                </button>
              </div>
            ) : null}

            <div className="space-y-2">
              <Label htmlFor="rule-name">Rule name</Label>
              <Input
                id="rule-name"
                value={form.name}
                onChange={(event) => patch({ name: event.target.value })}
                placeholder={form.kind === "earn" ? "e.g. Weekend earn" : "e.g. Checkout redemption"}
                className={fieldClass}
              />
            </div>

            {form.kind === "earn" ? (
              <>
                <div className="space-y-2">
                  <Label>How points are calculated</Label>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => patch({ basis: "points_per_order" })}
                      className={cn(
                        "rounded-2xl border px-3 py-3 text-left text-sm",
                        form.basis === "points_per_order"
                          ? "border-emerald-700 bg-emerald-50 font-medium text-emerald-950"
                          : "border-slate-200 text-slate-700",
                      )}
                    >
                      Points per order
                    </button>
                    <button
                      type="button"
                      onClick={() => patch({ basis: "invoice_percent" })}
                      className={cn(
                        "rounded-2xl border px-3 py-3 text-left text-sm",
                        form.basis === "invoice_percent"
                          ? "border-emerald-700 bg-emerald-50 font-medium text-emerald-950"
                          : "border-slate-200 text-slate-700",
                      )}
                    >
                      % of invoice amount
                    </button>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="min-invoice">Minimum invoice amount ({currency || "program currency"})</Label>
                    <Input
                      id="min-invoice"
                      type="number"
                      min={0}
                      value={form.minimumInvoice}
                      onChange={(event) => patch({ minimumInvoice: event.target.value })}
                      placeholder="200"
                      className={fieldClass}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="points-given">
                      {form.basis === "points_per_order"
                        ? "Points to give"
                        : "Percent of invoice"}
                    </Label>
                    <Input
                      id="points-given"
                      type="number"
                      min={1}
                      max={form.basis === "invoice_percent" ? 100 : undefined}
                      value={form.points}
                      onChange={(event) => patch({ points: event.target.value })}
                      placeholder={form.basis === "points_per_order" ? "10" : "5"}
                      className={fieldClass}
                    />
                  </div>
                </div>
                <p className="text-xs leading-relaxed text-slate-500">
                  Orders below the minimum invoice do not earn. Qualifying orders
                  receive{" "}
                  {form.basis === "points_per_order"
                    ? "a fixed number of points"
                    : "points equal to this percent of the invoice amount"}
                  .
                </p>
              </>
            ) : (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="min-points">Minimum points to redeem</Label>
                    <Input
                      id="min-points"
                      type="number"
                      min={1}
                      value={form.minimumPoints}
                      onChange={(event) => patch({ minimumPoints: event.target.value })}
                      placeholder="100"
                      className={fieldClass}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="max-points">Max points on one order</Label>
                    <Input
                      id="max-points"
                      type="number"
                      min={1}
                      value={form.maxPointsPerOrder}
                      onChange={(event) => patch({ maxPointsPerOrder: event.target.value })}
                      placeholder="2000"
                      className={fieldClass}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Usage limit</Label>
                  <Select
                    value={form.usageLimit}
                    onValueChange={(value: UsageLimit) => patch({ usageLimit: value })}
                  >
                    <SelectTrigger className={fieldClass}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="invoice_percent">% of invoice value</SelectItem>
                      <SelectItem value="balance_percent">% of points balance</SelectItem>
                      <SelectItem value="none">No limit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.usageLimit !== "none" ? (
                  <div className="space-y-2">
                    <Label htmlFor="usage-percent">
                      {form.usageLimit === "invoice_percent"
                        ? "Maximum share of the invoice (%)"
                        : "Maximum share of the points balance (%)"}
                    </Label>
                    <Input
                      id="usage-percent"
                      type="number"
                      min={1}
                      max={100}
                      value={form.usagePercent}
                      onChange={(event) => patch({ usagePercent: event.target.value })}
                      placeholder="30"
                      className={fieldClass}
                    />
                  </div>
                ) : (
                  <p className="text-xs leading-relaxed text-slate-500">
                    Redemption is capped only by the minimum and the maximum
                    points on one order.
                  </p>
                )}
                <div className="space-y-2">
                  <Label>Earn while redeem</Label>
                  <Select
                    value={form.earnWhileRedeem}
                    onValueChange={(value: EarnWhileRedeem) =>
                      patch({ earnWhileRedeem: value })
                    }
                  >
                    <SelectTrigger className={fieldClass}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EARN_WHILE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs leading-relaxed text-slate-500">
                    {
                      EARN_WHILE_OPTIONS.find((option) => option.value === form.earnWhileRedeem)
                        ?.hint
                    }
                  </p>
                </div>
              </>
            )}

            {formError ? (
              <p className="text-sm text-red-600">{formError}</p>
            ) : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditorOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-emerald-700 text-white hover:bg-emerald-800"
              onClick={saveRule}
            >
              {editingId ? "Save changes" : "Add rule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={viewing != null} onOpenChange={(open) => !open && setViewing(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          {viewing ? (
            <>
              <SheetHeader>
                <SheetTitle>{viewing.name}</SheetTitle>
                <SheetDescription>
                  {viewing.kind === "earn" ? "Earn rule" : "Redemption rule"}
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6">
                {viewing.kind === "earn" ? (
                  <>
                    <ReadRow label="Calculation" value={earnAward(viewing)} />
                    <ReadRow
                      label="Minimum invoice"
                      value={money(viewing.minimumInvoice, currency)}
                    />
                    <ReadRow
                      label="Points given"
                      value={
                        viewing.basis === "points_per_order"
                          ? pointsLabel(viewing.points)
                          : `${viewing.points}% of the invoice`
                      }
                    />
                  </>
                ) : (
                  <>
                    <ReadRow
                      label="Minimum points to redeem"
                      value={pointsLabel(viewing.minimumPoints)}
                    />
                    <ReadRow
                      label="Maximum on one order"
                      value={pointsLabel(viewing.maxPointsPerOrder)}
                    />
                    <ReadRow label="Usage limit" value={usageLabel(viewing)} />
                    <ReadRow
                      label="Earn while redeem"
                      value={earnWhileLabel(viewing.earnWhileRedeem)}
                    />
                  </>
                )}
              </div>
              <Button
                type="button"
                className="mt-6 bg-emerald-700 text-white hover:bg-emerald-800"
                onClick={() => {
                  const rule = viewing;
                  setViewing(null);
                  openEdit(rule);
                }}
              >
                Edit rule
              </Button>
            </>
          ) : null}
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={pendingDelete != null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this rule?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete
                ? `${pendingDelete.name} will be removed from the rules list.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={deleteRule}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
